import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spinner } from "@lemonadetraining/web-ui";
import ProductCard from "@lemonadetraining/web-shared/components/ProductCard";

/* firebase actions */
import { getUserLibrary } from "@lemonadetraining/web-shared";
import type { ProgrammeOrigin } from "../Common/privateProgramme";

const LIBRARY_ORIGIN: ProgrammeOrigin = "library";

interface ProgrammingItem {
  id: string;
  data: any;
}

interface ProgrammingSearchEngineProps {
  searchSubscriptions?: boolean;
}

const ProgrammingSearchEngine: React.FC<ProgrammingSearchEngineProps> = ({
  searchSubscriptions,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [subscribedProgramming, setSubscribedProgramming] = useState<ProgrammingItem[]>([]);

  /* --------- [Load users subscriptions from library] ---------*/
  useEffect(() => {
    if (!searchSubscriptions) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setFailed(false);
    getUserLibrary()
      .then((newData: any) => {
        if (!cancelled) setSubscribedProgramming(newData?.data ?? []);
      })
      .catch((error: any) => {
        console.warn("[Workout library] load failed", error);
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchSubscriptions, attempt]);

  return (
    <section className="flex flex-col gap-3" aria-labelledby="library-heading">
      <h2 id="library-heading" className="text-base font-semibold">
        Programs you follow
      </h2>
      {isLoading ? (
        <div className="m-5" data-testid="library-loading">
          <Spinner />
        </div>
      ) : failed ? (
        <div
          className="flex max-w-md flex-col items-start gap-2 rounded-xl border p-4"
          role="alert"
          data-testid="library-error"
        >
          <p className="text-sm">
            We couldn&apos;t load your programs. Check your connection and try again.
          </p>
          <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        </div>
      ) : subscribedProgramming.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="library-empty">
          No programs yet. When you join a program or your gym gives you one, it shows here.
        </p>
      ) : (
        searchSubscriptions && (
          <div className="flex flex-row flex-wrap gap-4">
            {subscribedProgramming.map((eachProgramming) => (
              <div key={eachProgramming.id}>
                <ProductCard
                  id={eachProgramming.id}
                  data={eachProgramming.data}
                  showAction={false}
                  onOpen={(pid) =>
                    navigate(`/dashboard/workout-programme/${pid}`, {
                      state: { programmeOrigin: LIBRARY_ORIGIN },
                    })
                  }
                />
              </div>
            ))}
          </div>
        )
      )}
    </section>
  );
};

export default ProgrammingSearchEngine;
